import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CloudUpload, Delete, Info } from '@mui/icons-material';
import { Button, Typography, Box, IconButton, Tooltip } from '@mui/material';


import styles from './file-uploader.module.scss';

interface FileUploaderProps {
  value: File | string | null;
  isDisabled?: boolean;
  onChange: (file: File | null) => void;
  setError: (msg: string) => void;
  maxFileSize?: number; // in bytes, default to 6MB
  allowedFileTypes?: string[]; // e.g., ['image/*', 'application/pdf']
  buttonText?: string;
  dragDropText?: string;
  fileNameDisplay?: string;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  value,
  isDisabled = false,
  onChange,
  setError,
  maxFileSize = 6 * 1024 * 1024, // 6MB default
  allowedFileTypes,
  buttonText,
  dragDropText,
  fileNameDisplay
}) => {
    const { t } = useTranslation();
    const defaultButtonText = t('clientAll.selectFile');
    const defaultDragDropText = t('clientAll.dragDropOrPaste');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [fileSizeFromUrl, setFileSizeFromUrl] = useState<string>('');

  // Получаем размер файла из URL
  useEffect(() => {
    if (typeof value === 'string' && value) {
      fetch(value, { method: 'HEAD' })
        .then(response => {
          const contentLength = response.headers.get('content-length');
          if (contentLength) {
            const size = parseInt(contentLength, 10);
            setFileSizeFromUrl(formatFileSize(size));
          } else {
            setFileSizeFromUrl('Размер неизвестен');
          }
        })
        .catch(() => {
          setFileSizeFromUrl('Размер неизвестен');
        });
    } else {
      setFileSizeFromUrl('');
    }
  }, [value]);

  const validateFile = useCallback((file: File): boolean => {
    // Проверка размера файла
    if (file.size > maxFileSize) {
      setError(t('clientAll.fileTooLarge', { maxFileSize: Math.round(maxFileSize / 1024 / 1024) }));
      return false;
    }

    // Проверка типа файла (если указаны ограничения)
    

    return true;
  }, [maxFileSize, allowedFileTypes, setError]);

  const handleFileSelect = useCallback((file: File) => {
    if (validateFile(file)) {
      onChange(file);
      setError(''); // Очищаем ошибки при успешной загрузке
    }
  }, [validateFile, onChange, setError]);

  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleButtonClick = useCallback(() => {
    if (!isDisabled) {
      fileInputRef.current?.click();
    }
  }, [isDisabled]);

  const handleRemoveFile = useCallback(() => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onChange]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileName = (): string => {
    if (value instanceof File) {
      return value.name;
    }
    if (typeof value === 'string' && value) {
      // Извлекаем имя файла из URL
      try {
        const url = new URL(value);
        const pathname = url.pathname;
        const filename = pathname.split('/').pop() || 'Файл';
        // Декодируем URL-encoded символы
        return decodeURIComponent(filename);
      } catch {
        return fileNameDisplay || t('clientAll.uploadedFile');
      }
    }
    return '';
  };

  const getFileSize = (): string => {
    if (value instanceof File) {
      return formatFileSize(value.size);
    }
    if (typeof value === 'string' && value) {
      return fileSizeFromUrl || t('clientAll.loading');
    }
    return '';
  };

  return (
    <div className={styles.fileUploader}>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        accept={allowedFileTypes?.join(',')}
        disabled={isDisabled}
      />

      {value ? (
        <Box className={styles.fileInfo}>
          <Box className={styles.fileDetails}>
            <Typography variant="body2" className={styles.fileName}>
              {getFileName()}
            </Typography>
            <Typography variant="caption" className={styles.fileSize}>
              {getFileSize()}
            </Typography>
          </Box>
          <Tooltip title={t('clientAll.deleteFile')}>
            <IconButton
              size="small"
              onClick={handleRemoveFile}
              disabled={isDisabled}
              className={styles.deleteButton}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <div
          className={`${styles.dropZone} ${isDragOver ? styles.dragOver : ''} ${isDisabled ? styles.disabled : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleButtonClick}
        >
          <CloudUpload className={styles.uploadIcon} />
          <Typography variant="body2" className={styles.dragText}>
            {dragDropText}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            disabled={isDisabled}
            className={styles.selectButton}
            onClick={(e) => {
              e.stopPropagation();
              handleButtonClick();
            }}
          >
            {buttonText}
          </Button>
        </div>
      )}

      <Box className={styles.infoSection}>
        <Info className={styles.infoIcon} />
        <Typography variant="caption" className={styles.infoText}>
          {t('clientAll.maxFileSizeMessage', { maxFileSize: Math.round(maxFileSize / 1024 / 1024) })}
        </Typography>
      </Box>
    </div>
  );
};

export default FileUploader;
